/**
 * **状态栏提示条** —— 从 Playground.vue 抽出来。
 *
 * ## 这个文件负责什么
 *
 * 一句话的即时反馈（「已复制 3 个节点」「已撤销：移动节点」）。
 * 这里只管**文案和定时器**；`lastNotice` 是另一条线（输出窗口的历史日志），
 * 由宿主注入，所以本文件不碰它。
 *
 * 提示条可以带**动作**（「撤销」按钮）—— 删框那种「要么连子节点一起删、
 * 要么撤销」的二选一就靠它，不用弹窗打断流程。
 *
 * ## ⚠️ 抽出来的方式：函数体逐字不动
 *
 * `notice` / `lastNotice` 由 `ctx` 注入；`noticeActions` / `noticeTimer`
 * 是本文件自己的状态，跟着一起搬走。
 */
import { ref } from 'vue'

export interface NoticeAction {
  label: string
  run: () => void
}

export interface NoticeCtx {
  /** 状态栏那行字（模板绑定） */
  notice: { value: string }
  /** 输出窗口里的最后一条（另一条线的状态，只写不读） */
  lastNotice: { value: string }
}

export function useNotice(ctx: NoticeCtx) {
  const { notice, lastNotice } = ctx

/** 状态栏短暂提示（5 秒后自动消失） */
let noticeTimer: number | undefined

/**
 * 提示条上的动作。
 *
 * ⚠️ 是**数组**而不是单个 —— 删框要「弹选项」：
 *   同时给出「连同 N 个子节点一起删」和「撤销」两个选择。
 * 只支持一个的话就得退回弹窗，那会打断流程。
 */
const noticeActions = ref<NoticeAction[]>([])

function clearNotice() {
  notice.value = ''
  noticeActions.value = []
  window.clearTimeout(noticeTimer)
}

function runNoticeAction(action: NoticeAction) {
  clearNotice()
  action.run()
}

function showNotice(message: string, actions?: NoticeAction | NoticeAction[]) {
  // 最顶上那行状态文字已经去掉了，统一在输出窗口里看
  lastNotice.value = message
  notice.value = message
  noticeActions.value = !actions ? [] : Array.isArray(actions) ? actions : [actions]
  window.clearTimeout(noticeTimer)
  noticeTimer = window.setTimeout(clearNotice, 5000)
}

  return { noticeActions, clearNotice, runNoticeAction, showNotice }
}
